import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useSwitchStudentBillingMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import type { BillingTypeV2, StudentBillingCurrent } from "./StudentBillingTypeSection";
import { acceptCurrencyKeystroke } from "./studentBillingValidation";

/**
 * The one-and-only entry point for changing a student's billing arrangement
 * post-create. The Student Edit form opens this modal instead of PUTing
 * billing fields directly, so:
 *   - the admin sees the current arrangement vs the requested one
 *   - the required "future invoices only" confirmation checkbox is enforced
 *   - the API call is the dedicated /billing/switch endpoint that appends a
 *     history row rather than mutating the student's current row silently.
 *
 * Never falls back to the legacy student PUT on failure — a failed switch is
 * a failed switch. Backend errors surface inline; the modal stays open.
 */

export interface SwitchStudentBillingModalProps {
  open: boolean;
  onClose: () => void;
  studentId: number | string;
  current: StudentBillingCurrent | null;
  /** Prefill from the section on Edit page so the modal opens showing the admin's intended values. */
  proposed?: {
    billing_type_v2: BillingTypeV2;
    monthly_amount: number | null;
    billing_effective_from: string | null;
    tax_applicable: boolean;
  } | null;
  onSuccess?: () => void;
}

const fmtType = (t: BillingTypeV2 | null): string =>
  t === "monthly"
    ? "Monthly Billing"
    : t === "class_wise"
      ? "Class-Wise Billing"
      : "Not set";

const fmtMoney = (v: number | null | undefined): string =>
  v === null || v === undefined || Number.isNaN(v) ? "—" : `$${Number(v).toFixed(2)}`;

const SwitchStudentBillingModal: React.FC<SwitchStudentBillingModalProps> = ({
  open,
  onClose,
  studentId,
  current,
  proposed,
  onSuccess,
}) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [switchBilling, { isLoading }] = useSwitchStudentBillingMutation();

  const [newType, setNewType] = useState<BillingTypeV2>(
    proposed?.billing_type_v2 || current?.billing_type || "monthly"
  );
  const [monthlyAmount, setMonthlyAmount] = useState<string>(
    proposed?.monthly_amount !== null && proposed?.monthly_amount !== undefined
      ? String(proposed.monthly_amount)
      : current?.monthly_amount !== null && current?.monthly_amount !== undefined
        ? String(current.monthly_amount)
        : ""
  );
  const [effectiveFrom, setEffectiveFrom] = useState<Dayjs | null>(
    proposed?.billing_effective_from
      ? dayjs(proposed.billing_effective_from)
      : dayjs()
  );
  const [taxApplicable, setTaxApplicable] = useState<boolean>(
    proposed?.tax_applicable ?? current?.tax_applicable ?? false
  );
  const [changeReason, setChangeReason] = useState<string>("");
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Reset when reopened with different proposed/current values.
  useEffect(() => {
    if (!open) return;
    setNewType(proposed?.billing_type_v2 || current?.billing_type || "monthly");
    setMonthlyAmount(
      proposed?.monthly_amount !== null && proposed?.monthly_amount !== undefined
        ? String(proposed.monthly_amount)
        : current?.monthly_amount !== null && current?.monthly_amount !== undefined
          ? String(current.monthly_amount)
          : ""
    );
    setEffectiveFrom(
      proposed?.billing_effective_from
        ? dayjs(proposed.billing_effective_from)
        : dayjs()
    );
    setTaxApplicable(proposed?.tax_applicable ?? current?.tax_applicable ?? false);
    setChangeReason("");
    setConfirmed(false);
    setApiError(null);
  }, [open, proposed, current]);

  const isSwitchingType =
    !!current?.billing_type && current.billing_type !== newType;

  const message = useMemo(() => {
    if (!isSwitchingType) {
      return "You are updating the billing amount or effective date. Future invoices will use the new values.";
    }
    if (current?.billing_type === "monthly" && newType === "class_wise") {
      return "You are switching this student from monthly billing to class-wise billing. Future invoices will use the fees configured on the assigned classes.";
    }
    return "You are switching this student from class-wise billing to monthly billing. Future invoices will use the monthly amount, and individual class fees will no longer be charged separately.";
  }, [current, newType, isSwitchingType]);

  const validationError = useMemo((): string | null => {
    if (!effectiveFrom) return "Effective Date is required.";
    if (newType === "monthly") {
      // Validate the numeric form of the raw string, never the display.
      const amount = Number(monthlyAmount);
      if (!monthlyAmount || !Number.isFinite(amount) || amount <= 0) {
        return "Monthly Amount must be greater than 0 for Monthly Billing.";
      }
    }
    if (!changeReason.trim()) return "Please provide a change reason.";
    if (!confirmed)
      return "Please confirm you understand this change applies to future invoices only.";
    return null;
  }, [newType, monthlyAmount, effectiveFrom, changeReason, confirmed]);

  const handleSubmit = async () => {
    setApiError(null);
    if (validationError) return;
    try {
      const res = await switchBilling({
        studentId,
        body: {
          billing_type: newType,
          monthly_amount:
            newType === "monthly" ? Number(monthlyAmount) : null,
          tax_applicable: !!taxApplicable,
          effective_from: effectiveFrom!.format("YYYY-MM-DD"),
          change_reason: changeReason.trim(),
          confirmed: true,
        },
      }).unwrap();
      if (!res?.status) {
        setApiError(res?.message || "Failed to switch billing arrangement.");
        return;
      }
      showMessage(res.message || "Billing arrangement updated.");
      onSuccess?.();
      onClose();
    } catch (e: any) {
      const msg =
        e?.data?.message ||
        e?.message ||
        (typeof e === "string" ? e : "Failed to switch billing arrangement.");
      setApiError(msg);
      // No fallback to legacy PUT — a failed switch stays visible.
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningAmberOutlinedIcon color="warning" />
        Switch Billing Arrangement
      </DialogTitle>
      <DialogContent dividers>
        <Alert
          severity="warning"
          icon={<WarningAmberOutlinedIcon />}
          sx={{ mb: 2 }}
        >
          Already generated, finalized, partially paid, and paid invoices will
          remain unchanged. This change applies to future invoice generation
          only.
        </Alert>

        <Typography variant="body2" sx={{ mb: 2 }}>
          {message}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
                border: (t) => `1px solid ${t.palette.divider}`,
                height: "100%",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Current Billing
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Typography variant="body2">
                  <strong>Type:</strong> {fmtType(current?.billing_type ?? null)}
                </Typography>
                {current?.billing_type === "monthly" && (
                  <Typography variant="body2">
                    <strong>Monthly Amount:</strong>{" "}
                    {fmtMoney(current?.monthly_amount)}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Effective Date:</strong>{" "}
                  {current?.effective_from ?? "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Tax:</strong>{" "}
                  {current?.tax_applicable ? "Applicable" : "Exempt"}
                </Typography>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                border: (t) => `1px solid ${alpha("#0ea5e9", 0.2)}`,
                height: "100%",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                New Billing
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.75 }}>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label="Monthly"
                    color={newType === "monthly" ? "primary" : "default"}
                    variant={newType === "monthly" ? "filled" : "outlined"}
                    onClick={() => setNewType("monthly")}
                    size="small"
                  />
                  <Chip
                    label="Class-Wise"
                    color={newType === "class_wise" ? "primary" : "default"}
                    variant={newType === "class_wise" ? "filled" : "outlined"}
                    onClick={() => {
                      setNewType("class_wise");
                      // Clear the amount so nothing stale gets validated
                      // or posted with the class-wise switch.
                      setMonthlyAmount("");
                    }}
                    size="small"
                  />
                </Stack>
                {newType === "monthly" && (
                  <TextField
                    label="Monthly Amount"
                    size="small"
                    type="number"
                    fullWidth
                    name="monthly_amount"
                    inputProps={{
                      min: "0.01",
                      step: "0.01",
                      inputMode: "decimal",
                      pattern: "^\\d+(\\.\\d{1,2})?$",
                    }}
                    value={monthlyAmount}
                    onChange={(e) => {
                      // Store the raw string — $ is a visual adornment
                      // only, and Number() runs at submit time.
                      const next = acceptCurrencyKeystroke(e.target.value);
                      if (next === null) return;
                      setMonthlyAmount(next);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                )}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Effective Date"
                    value={effectiveFrom}
                    onChange={(v) => setEffectiveFrom(v)}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />
                </LocalizationProvider>
                <FormControlLabel
                  control={
                    <Switch
                      checked={taxApplicable}
                      onChange={(_, c) => setTaxApplicable(c)}
                    />
                  }
                  label="Tax Applicable"
                />
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Change Reason"
              placeholder="Why is this change being made?"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(_, c) => setConfirmed(c)}
                />
              }
              label="I understand that this change applies to future invoice generation only."
            />
          </Grid>
        </Grid>

        {apiError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiError}
          </Alert>
        )}
        {!apiError && validationError && (
          <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: 12 }}>
            {validationError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isLoading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || !!validationError}
          variant="contained"
          color="warning"
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {isLoading ? "Applying…" : "Confirm Switch"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SwitchStudentBillingModal;
